var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import CodeGen, { CodeGenLanguage } from '../agent/code-gen';
import ContextProcessor from '../agent/context-processor';
import ExternalResourceDirectory from '../agent/external-resource-directory';
import ExternalResourceEvaluator from '../agent/external-resource-evaluator';
import { stringifyContext } from '../utils/context';
import { dereferencePath } from '../utils/openapi/dereference-path';
import { operationSchemas } from '../utils/openapi/operation';
import { extractRequiredSchema } from '../utils/openapi/required-schema';
import { t } from '../utils/template';
export const objectivePrefix = (params, withContext = true) => t([
    "I have this objective: '''{{objective}}'''",
    '{{#if showContext}}I also have some historical contextual data, think of these as variables that can be used to achieve the given objective.',
    'This context is represented as the following JSON schema:',
    '```{{context}}```{{/if}}',
], Object.assign(Object.assign({}, params), { context: stringifyContext(params.context), showContext: withContext && !!Object.keys(params.context).length }));
export const operationPrefix = (params, skipFirstLine = false) => t([
    skipFirstLine ? '' : 'I have decided to call this external resource:',
    'Provider: {{provider}}',
    'HTTP path: {{path}}',
    'HTTP method: {{method}}',
    '{{#if description}}description: {{description}}{{/if}}',
], params);
export default class Parser {
    constructor(args) {
        this.parse = (objective, context, openAPIs) => __awaiter(this, void 0, void 0, function* () {
            for (const openAPI of openAPIs) {
                yield this.externalResourceDirectory.embed(openAPI);
            }
            const shortlist = yield this.externalResourceDirectory.shortlist(objective, context, openAPIs);
            this.logger.info('Here are the APIs we are shortlisting for you: \n');
            this.logger.log(shortlist);
            for (const api of shortlist) {
                const openapi = openAPIs.find((o) => o.info.title === api.provider);
                const operationObject = yield dereferencePath(openapi, api.method, api.path);
                if (!operationObject)
                    continue;
                const operationMetadata = {
                    objective,
                    context,
                    provider: api.provider,
                    path: api.path,
                    method: api.method,
                    description: api.description,
                };
                const { bodySchema, querySchema, pathSchema, requestContentType, responseContentType, responseSchema, } = operationSchemas(operationObject);
                const requiredBodySchema = extractRequiredSchema(bodySchema);
                const requiredQuerySchema = extractRequiredSchema(querySchema);
                const requiredPathSchema = extractRequiredSchema(pathSchema);
                const [isFeasible, reason] = yield this.externalResourceEvaluator.isFeasible(Object.assign(Object.assign({}, operationMetadata), { requiredInputSchema: {
                        body: requiredBodySchema,
                        query: requiredQuerySchema,
                        path: requiredPathSchema,
                    } }));
                if (!isFeasible) {
                    this.logger.warn(`chosen API (${api.provider}: ${api.method} ${api.path}) is not feasible, moving on to the next API. reason: '${reason}'`);
                    continue;
                }
                this.logger.log(`Chosen API (${api.provider}: ${api.method} ${api.path}) is feasible, evaluating it further.`);
                this.logger.log('Narrowing down the parameter schemas for the API...');
                const { body: filteredBodySchema, query: filteredQuerySchema, path: filteredPathSchema, } = yield this.externalResourceEvaluator.filterInputSchemas(Object.assign(Object.assign({}, operationMetadata), { requiredInputSchema: {
                        body: requiredBodySchema,
                        query: requiredQuerySchema,
                        path: requiredPathSchema,
                    }, inputSchema: {
                        body: bodySchema,
                        query: querySchema,
                        path: pathSchema,
                    } }));
                this.logger.info({
                    filteredBodySchema,
                    filteredQuerySchema,
                    filteredPathSchema,
                });
                this.logger.log('Narrowing down the historical context that can be used in the API call...');
                const { filteredContextForBody, filteredContextForQuery, filteredContextForPath, } = yield this.contextProcessor.filter(Object.assign(Object.assign({}, operationMetadata), { inputSchema: {
                        body: filteredBodySchema,
                        query: filteredQuerySchema,
                        path: filteredPathSchema,
                    } }));
                this.logger.info({
                    filteredContextForBody,
                    filteredContextForQuery,
                    filteredContextForPath,
                });
                this.logger.log('Generating the input parameters...');
                const bodyParams = yield this.codeGen.generateInput(Object.assign(Object.assign({}, operationMetadata), { inputSchema: filteredBodySchema, filteredContext: filteredContextForBody, name: 'body' }));
                const queryParams = yield this.codeGen.generateInput(Object.assign(Object.assign({}, operationMetadata), { inputSchema: filteredQuerySchema, filteredContext: filteredContextForQuery, name: 'query' }));
                const pathParams = yield this.codeGen.generateInput(Object.assign(Object.assign({}, operationMetadata), { inputSchema: filteredPathSchema, filteredContext: filteredContextForPath, name: 'path' }));
                yield this.logger.log(`Generating the ${this.codeGen.language} expression...`);
                return {
                    provider: api.provider,
                    method: api.method,
                    path: api.path,
                    bodyParams,
                    queryParams,
                    pathParams,
                    servers: 'servers' in openapi ? openapi.servers : undefined,
                    requestContentType,
                    responseContentType,
                    responseSchema,
                };
            }
        });
        const { language = CodeGenLanguage.javascript, embeddingFunctions } = args;
        this.logger = args.logger;
        this.llm = args.llm;
        this.externalResourceDirectory = new ExternalResourceDirectory(this, embeddingFunctions);
        this.externalResourceEvaluator = new ExternalResourceEvaluator(this);
        this.contextProcessor = new ContextProcessor(this);
        this.codeGen = new CodeGen(this, language);
    }
}
